import { useMemo, useRef, useState } from 'react'
import { Check, Search, Video } from 'lucide-react'
import { Spinner } from '@/components/ui'
import { useClickOutside, useEscapeKey } from '@/hooks'
import { formatDayLabel } from '@/internationalization/format'
import { cn } from '@/internationalization/cn'
import { useActionItemMeetings } from '../hooks'

/**
 * Filter the action list by the meeting a commitment came out of.
 *
 * A SEARCHABLE popover rather than a `Dropdown`, unlike the patron filter: a
 * workspace records hundreds of meetings a year and half of them are called
 * some variation of "weekly", so scrolling a list is not how anyone finds one.
 * The same shape as `AssigneePicker` — one cached request, filtered as you
 * type — because a filter that narrows only after a round trip feels broken.
 *
 * THE OPTIONS ARE THE MEETINGS THAT HAVE ACTION ITEMS, not every meeting. A
 * meeting where nobody committed to anything is a dead option, and picking one
 * reads as the filter being broken rather than as the meeting being quiet. The
 * counts are shown for the same reason: they say which options are worth a
 * click before you spend one.
 *
 * The date is shown beside the title and is not decoration — it is the only
 * thing that tells nine meetings called "Teams bot meeting" apart.
 */
export function MeetingFilter({
  value,
  onChange,
}: {
  /** A meeting id, or 'all'. */
  value: string
  onChange: (next: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const close = () => {
    setIsOpen(false)
    setQuery('')
  }
  useClickOutside(containerRef, close, isOpen)
  useEscapeKey(close, isOpen)

  const { data, isPending, error } = useActionItemMeetings(isOpen)

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return data ?? []
    return (data ?? []).filter((meeting) => meeting.title.toLowerCase().includes(term))
  }, [data, query])

  // The label has to survive the list not being loaded: the control renders
  // before it is ever opened, and after a reload the only thing known about
  // the selection is its id. "Meeting" is that honest gap — never "All".
  const selected = (data ?? []).find((meeting) => meeting.id === value)
  const label = value === 'all' ? 'Any meeting' : (selected?.title ?? 'Meeting')

  const choose = (next: string) => {
    onChange(next)
    close()
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          'inline-flex h-8 max-w-[16rem] items-center gap-1.5 rounded-md border border-border px-2.5 text-sm',
          'text-fg-muted hover:bg-surface-raised hover:text-fg',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand',
          value !== 'all' && 'border-brand/40 bg-brand/5 text-fg',
        )}
      >
        <Video aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Filter by meeting"
          className="absolute left-0 z-30 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="border-b border-border p-2">
            <label className="relative block">
              <span className="sr-only">Search meetings</span>
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle"
              />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search meetings…"
                className="w-full rounded-md border border-border bg-surface-sunken py-1.5 pl-7 pr-2 text-sm text-fg placeholder:text-fg-subtle focus:outline focus:outline-2 focus:outline-brand"
              />
            </label>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            <Option
              label="Any meeting"
              isSelected={value === 'all'}
              onSelect={() => choose('all')}
            />

            {isPending && (
              <p className="flex items-center gap-2 px-3 py-2 text-sm text-fg-muted">
                <Spinner size="sm" />
                Loading meetings…
              </p>
            )}

            {error && (
              <p className="px-3 py-2 text-sm text-danger">
                We could not load the meeting list.
              </p>
            )}

            {!isPending && !error && matches.length === 0 && (
              <p className="px-3 py-2 text-sm text-fg-muted">
                {query.trim()
                  ? 'No meeting matches that.'
                  : 'No meeting has action items yet.'}
              </p>
            )}

            {matches.map((meeting) => (
              <Option
                key={meeting.id}
                label={meeting.title}
                /* Two counts would be noise; the open one is what somebody
                   filtering an action list is actually after. */
                meta={[
                  meeting.startsAt ? formatDayLabel(meeting.startsAt) : null,
                  meeting.openActionItemCount > 0
                    ? `${meeting.openActionItemCount} open`
                    : `${meeting.actionItemCount} done`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                isSelected={value === meeting.id}
                onSelect={() => choose(meeting.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Option({
  label,
  meta,
  isSelected,
  onSelect,
}: {
  label: string
  meta?: string
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onClick={onSelect}
      className="flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm text-fg hover:bg-surface-raised"
    >
      <span className="w-3.5 shrink-0 pt-0.5">
        {isSelected && <Check aria-hidden className="h-3.5 w-3.5 text-brand" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate">{label}</span>
        {meta && <span className="block truncate text-xs text-fg-subtle">{meta}</span>}
      </span>
    </button>
  )
}
